/**
 * QIDO - Query based on ID for DICOM Objects
 * search for studies, series and instances by patient ID, and receive their
 * unique identifiers for further usage.
 *
 * Quick: https://www.dicomstandard.org/dicomweb/query-qido-rs/
 * Standard: http://dicom.nema.org/medical/dicom/current/output/html/part18.html#sect_10.6
 *
 * Routes:
 * ==========
 * /studies?
 * /studies/{studyInstanceUid}/series?
 * /studies/{studyInstanceUid}/series/{seriesInstanceUid}/instances?
 *
 * Query Parameters:
 * ================
 * | KEY              | VALUE              |
 * |------------------|--------------------|
 * | {attributeId}    | {value}            |
 * | includeField     | {attribute} or all |
 * | fuzzymatching    | true OR false      |
 * | limit            | {number}           |
 * | offset           | {number}           |
 */
import { DICOMWeb, utils } from '@ohif/core';
import { sortStudySeries } from '@ohif/core/src/utils/sortStudy';
import { WORKLIST_GENDER_FROM_API_TOKEN } from './constants/genderDisplay.js';

const { getString, getName, getModalities } = DICOMWeb;

function getOptionalPlainField(obj, keys) {
  for (const candidateKey of keys) {
    const rawValue = obj[candidateKey];
    if (rawValue === undefined || rawValue === null) {
      continue;
    }
    if (typeof rawValue === 'string' && rawValue.trim()) {
      return rawValue.trim();
    }
    if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
      return String(rawValue);
    }
    if (rawValue && rawValue.Value && Array.isArray(rawValue.Value) && rawValue.Value[0] != null) {
      const firstElement = rawValue.Value[0];
      const valueText = typeof firstElement === 'string' ? firstElement : String(firstElement);
      if (valueText.trim()) {
        return valueText.trim();
      }
    }
  }
  return '';
}

/** ISO yyyy-MM-dd or digits (YYYYMMDD / with separators) */
function parseBirthDateString(rawDateString) {
  if (!rawDateString || typeof rawDateString !== 'string') {
    return null;
  }
  const trimmedDate = rawDateString.trim();
  if (!trimmedDate) {
    return null;
  }
  const isoMatch = trimmedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const parsedDate = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }
  const digitOnly = trimmedDate.replace(/\D/g, '');
  if (digitOnly.length >= 8) {
    const yearPart = digitOnly.slice(0, 4);
    const monthPart = digitOnly.slice(4, 6);
    const dayPart = digitOnly.slice(6, 8);
    const parsedFromDigits = new Date(Number(yearPart), Number(monthPart) - 1, Number(dayPart));
    return Number.isNaN(parsedFromDigits.getTime()) ? null : parsedFromDigits;
  }
  return null;
}

/** Whole years completed at reference date (local “today” for worklist demographics). */
function ageYearsCompleted(birth, ref) {
  if (!birth || !ref) {
    return null;
  }
  let years = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    years--;
  }
  if (years < 0 || years > 150) {
    return null;
  }
  return years;
}

/** RIS gender text → short label (Figma-style with DICOM codes). */
function normalizeGenderDisplay(apiGender) {
  const trimmedGender = apiGender && String(apiGender).trim();
  if (!trimmedGender) {
    return '';
  }
  const lowerToken = trimmedGender.toLowerCase();
  const normalizedFromToken = WORKLIST_GENDER_FROM_API_TOKEN[lowerToken];
  if (normalizedFromToken !== undefined) {
    return normalizedFromToken;
  }
  if (trimmedGender.length === 1) {
    return trimmedGender.toUpperCase();
  }
  return trimmedGender;
}

/**
 * Parses resulting data from a QIDO call into a set of Study MetaData
 *
 * @param {Array} qidoStudies - An array of study objects. Each object contains a keys for DICOM tags.
 * @param {object} qidoStudies[0].qidoStudy - An object where each key is the DICOM Tag group+element
 * @param {object} qidoStudies[0].qidoStudy[dicomTag] - Optional object that represents DICOM Tag
 * @param {string} qidoStudies[0].qidoStudy[dicomTag].vr - Value Representation
 * @param {string[]} qidoStudies[0].qidoStudy[dicomTag].Value - Optional string array representation of the DICOM Tag's value
 * @returns {Array} An array of Study MetaData objects
 */
function processResults(qidoStudies) {
  if (!qidoStudies || !qidoStudies.length) {
    return [];
  }

  const studies = [];

  qidoStudies.forEach(qidoStudy => {
    // Extract report status - handle both string and DICOM tag object format
    let reportStatus = '';
    if (qidoStudy.reportStatus !== undefined) {
      if (typeof qidoStudy.reportStatus === 'string') {
        reportStatus = qidoStudy.reportStatus;
      } else if (
        qidoStudy.reportStatus &&
        qidoStudy.reportStatus.Value &&
        Array.isArray(qidoStudy.reportStatus.Value)
      ) {
        reportStatus = qidoStudy.reportStatus.Value[0] || '';
      }
    } else if (qidoStudy.report_status !== undefined) {
      if (typeof qidoStudy.report_status === 'string') {
        reportStatus = qidoStudy.report_status;
      } else if (
        qidoStudy.report_status &&
        qidoStudy.report_status.Value &&
        Array.isArray(qidoStudy.report_status.Value)
      ) {
        reportStatus = qidoStudy.report_status.Value[0] || '';
      }
    }

    // Extract assigned name - handle both string and DICOM tag object format
    let assignedName = '';
    if (qidoStudy.assigned_name !== undefined) {
      if (typeof qidoStudy.assigned_name === 'string') {
        assignedName = qidoStudy.assigned_name;
      } else if (
        qidoStudy.assigned_name &&
        qidoStudy.assigned_name.Value &&
        Array.isArray(qidoStudy.assigned_name.Value)
      ) {
        assignedName = qidoStudy.assigned_name.Value[0] || '';
      }
    } else if (qidoStudy.assignedName !== undefined) {
      if (typeof qidoStudy.assignedName === 'string') {
        assignedName = qidoStudy.assignedName;
      } else if (
        qidoStudy.assignedName &&
        qidoStudy.assignedName.Value &&
        Array.isArray(qidoStudy.assignedName.Value)
      ) {
        assignedName = qidoStudy.assignedName.Value[0] || '';
      }
    }

    // Extract assigned_hub_facility_id - handle both string/number and DICOM tag object format
    const assignedHubFacilityId = getOptionalPlainField(qidoStudy, [
      'assigned_hub_facility_id',
      'assignedHubFacilityId',
    ]);
    const assignedHubFacilityName = getOptionalPlainField(qidoStudy, [
      'assigned_hub_facility_name',
      'assignedHubFacilityName',
    ]);
    const sourceFacilityId = getOptionalPlainField(qidoStudy, [
      'source_facility_id',
      'sourceFacilityId',
    ]);
    const sourceFacilityName = getOptionalPlainField(qidoStudy, [
      'source_facility_name',
      'sourceFacilityName',
    ]);

    // Extract study_id - handle both string and DICOM tag object format
    let studyId = null;
    if (qidoStudy.study_id !== undefined) {
      if (typeof qidoStudy.study_id === 'string' || typeof qidoStudy.study_id === 'number') {
        studyId = qidoStudy.study_id;
      } else if (
        qidoStudy.study_id &&
        qidoStudy.study_id.Value &&
        Array.isArray(qidoStudy.study_id.Value)
      ) {
        const studyIdValue = qidoStudy.study_id.Value[0];
        if (studyIdValue && studyIdValue.toString().trim() !== '') {
          studyId = studyIdValue;
        }
      }
    }

    // Extract is_critical - handle both boolean and DICOM tag object format
    let isCritical = false;
    if (qidoStudy.is_critical !== undefined) {
      if (typeof qidoStudy.is_critical === 'boolean') {
        isCritical = qidoStudy.is_critical;
      } else if (
        qidoStudy.is_critical &&
        qidoStudy.is_critical.Value &&
        Array.isArray(qidoStudy.is_critical.Value)
      ) {
        const criticalValue = qidoStudy.is_critical.Value[0];
        isCritical = criticalValue === true || criticalValue === 'true' || criticalValue === 1;
      }
    }

    // Extract critical_reason - handle both string and DICOM tag object format
    let criticalReason = '';
    if (qidoStudy.critical_reason !== undefined) {
      if (typeof qidoStudy.critical_reason === 'string') {
        criticalReason = qidoStudy.critical_reason.trim();
      } else if (
        qidoStudy.critical_reason &&
        qidoStudy.critical_reason.Value &&
        Array.isArray(qidoStudy.critical_reason.Value)
      ) {
        const reasonValue = qidoStudy.critical_reason.Value[0];
        criticalReason = reasonValue ? String(reasonValue).trim() : '';
      }
    }

    // referring_physician from API (custom field, same shape as assigned_name)
    let referringPhysician = '';
    if (qidoStudy.referring_physician !== undefined) {
      if (typeof qidoStudy.referring_physician === 'string') {
        referringPhysician = qidoStudy.referring_physician.trim();
      } else if (
        qidoStudy.referring_physician &&
        qidoStudy.referring_physician.Value &&
        Array.isArray(qidoStudy.referring_physician.Value)
      ) {
        const v = qidoStudy.referring_physician.Value[0];
        referringPhysician = v != null && v !== '' ? String(v).trim() : '';
      }
    } else if (qidoStudy.referringPhysician !== undefined) {
      if (typeof qidoStudy.referringPhysician === 'string') {
        referringPhysician = qidoStudy.referringPhysician.trim();
      } else if (
        qidoStudy.referringPhysician &&
        qidoStudy.referringPhysician.Value &&
        Array.isArray(qidoStudy.referringPhysician.Value)
      ) {
        const v = qidoStudy.referringPhysician.Value[0];
        referringPhysician = v != null && v !== '' ? String(v).trim() : '';
      }
    }

    const dicomSex = getString(qidoStudy['00100040']) || '';
    const apiGender = getOptionalPlainField(qidoStudy, ['gender']);
    const patientSex = normalizeGenderDisplay(apiGender) || dicomSex;

    const refDate = new Date();
    const birthDateApi = getOptionalPlainField(qidoStudy, ['birth_date', 'birthDate']);
    const dobDicom = getString(qidoStudy['00100030']) || '';
    let patientAge = getString(qidoStudy['00101010']) || getString(qidoStudy['00101040']) || '';
    const birthFromApi = parseBirthDateString(birthDateApi);
    const birthFromDicom = !birthFromApi && dobDicom ? parseBirthDateString(dobDicom) : null;
    const birthForAge = birthFromApi || birthFromDicom;
    if (birthForAge) {
      const years = ageYearsCompleted(birthForAge, refDate);
      if (years !== null) {
        patientAge = String(years);
      }
    }
    const clinicalHistory = getOptionalPlainField(qidoStudy, [
      'clinical_history',
      'clinicalHistory',
      'reason_for_visit',
      'reasonForVisit',
    ]);
    const priority = getOptionalPlainField(qidoStudy, [
      'priority',
      'study_priority',
      'studyPriority',
      'requested_procedure_priority',
      'requestedProcedurePriority',
    ]);
    const procedureDescription = getOptionalPlainField(qidoStudy, [
      'procedure_description',
      'procedureDescription',
      'requested_procedure_description',
    ]);
    const testName = getOptionalPlainField(qidoStudy, ['test_name', 'testName']);
    const worklistPriorityRuleName = getOptionalPlainField(qidoStudy, [
      'worklist_priority_rule_name',
      'worklistPriorityRuleName',
    ]);
    const appointmentType = getOptionalPlainField(qidoStudy, [
      'appointment_type',
      'appointmentType',
    ]);
    const aiStatus = getOptionalPlainField(qidoStudy, ['ai_status', 'aiStatus']) || '';
    const aiClassification =
      getOptionalPlainField(qidoStudy, ['ai_classification', 'aiClassification']) || '';
    const aiModelVersion =
      getOptionalPlainField(qidoStudy, ['ai_model_version', 'aiModelVersion']) || '';
    const reportDateTime = getOptionalPlainField(qidoStudy, ['report_datetime']) || '';

    studies.push({
      studyInstanceUid: getString(qidoStudy['0020000D']),
      date: getString(qidoStudy['00080020']), // YYYYMMDD
      time: getString(qidoStudy['00080030']), // HHmmss.SSS (24-hour, minutes, seconds, fractional seconds)
      accession: getString(qidoStudy['00080050']) || '', // short string, probably a number?
      mrn:
        getString(qidoStudy['00100020']) ||
        getOptionalPlainField(qidoStudy, ['patient_id', 'patientId', 'uhid', 'UHID']) ||
        '',
      patientName: utils.formatPN(getName(qidoStudy['00100010'])) || '',
      patientSex,
      patientAge,
      clinicalHistory,
      priority,
      procedureDescription,
      testName,
      worklistPriorityRuleName,
      instances: Number(getString(qidoStudy['00201208'])) || 0, // number
      description: getString(qidoStudy['00081030']) || '',
      modalities: getString(getModalities(qidoStudy['00080060'], qidoStudy['00080061'])) || '',
      reportStatus: reportStatus,
      assignedName: assignedName,
      assignedHubFacilityId: assignedHubFacilityId,
      assignedHubFacilityName: assignedHubFacilityName,
      sourceFacilityId: sourceFacilityId,
      sourceFacilityName: sourceFacilityName,
      studyId: studyId, // Add study_id to study object
      isCritical: isCritical, // Add is_critical to study object
      criticalReason: criticalReason, // Add critical_reason to study object
      referringPhysician: referringPhysician,
      appointmentType: appointmentType,
      aiStatus,
      aiClassification,
      aiModelVersion,
      reportDateTime,
    });
  });

  return studies;
}

/**
 * Parses resulting data from a QIDO call into a set of Study MetaData
 *
 * @param {Array} qidoSeries - An array of study objects. Each object contains a keys for DICOM tags.
 * @param {object} qidoSeries[0].qidoSeries - An object where each key is the DICOM Tag group+element
 * @param {object} qidoSeries[0].qidoSeries[dicomTag] - Optional object that represents DICOM Tag
 * @param {string} qidoSeries[0].qidoSeries[dicomTag].vr - Value Representation
 * @param {string[]} qidoSeries[0].qidoSeries[dicomTag].Value - Optional string array representation of the DICOM Tag's value
 * @returns {Array} An array of Study MetaData objects
 */
export function processSeriesResults(qidoSeries) {
  const series = [];

  if (qidoSeries && qidoSeries.length) {
    qidoSeries.forEach(qidoSeries =>
      series.push({
        studyInstanceUid: getString(qidoSeries['0020000D']),
        seriesInstanceUid: getString(qidoSeries['0020000E']),
        modality: getString(qidoSeries['00080060']),
        seriesNumber: getString(qidoSeries['00200011']),
        seriesDate: utils.formatDate(getString(qidoSeries['00080021'])),
        numSeriesInstances: Number(getString(qidoSeries['00201209'])),
        description: getString(qidoSeries['0008103E']),
      })
    );
  }

  sortStudySeries(series);

  return series;
}

/**
 *
 * @param {object} dicomWebClient - Client similar to what's provided by `dicomweb-client` library
 * @param {function} dicomWebClient.searchForStudies -
 * @param {string} [studyInstanceUid]
 * @param {string} [seriesInstanceUid]
 * @param {string} [queryParamaters]
 * @returns {Promise<results>} - Promise that resolves results
 */
async function search(dicomWebClient, studyInstanceUid, seriesInstanceUid, queryParameters) {
  let searchResult = await dicomWebClient.searchForStudies({
    studyInstanceUid: undefined,
    queryParams: queryParameters,
  });

  return searchResult;
}

/**
 *
 * @param {string} studyInstanceUID - ID of study to return a list of series for
 * @returns {Promise} - Resolves SeriesMetadata[] in study
 */
export function seriesInStudy(dicomWebClient, studyInstanceUID) {
  // Series Description
  // Already included?
  const commaSeparatedFields = ['0008103E', '00080021'].join(',');
  const queryParams = {
    includefield: commaSeparatedFields,
  };

  return dicomWebClient.searchForSeries({ studyInstanceUID, queryParams });
}

export default function searchStudies(server, filter) {
  const queryParams = getQIDOQueryParams(filter, server.qidoSupportsIncludeField);
  const options = {
    queryParams,
  };

  return dicomWeb.searchForStudies(options).then(resultDataToStudies);
}

/**
 * Produces a QIDO URL given server details and a set of specified search filter
 * items
 *
 * @param filter
 * @param serverSupportsQIDOIncludeField
 * @returns {string} The URL with encoded filter query data
 */
function mapParams(params, options = {}) {
  if (!params) {
    return;
  }
  const commaSeparatedFields = [
    '00081030', // Study Description
    '00080060', // Modality (often empty at study level)
    '00080061', // Modalities in Study (MR/CT list for worklist chips)
    // Add more fields here if you want them in the result
  ].join(',');

  const useWildcard =
    params?.disableWildcard !== undefined ? !params.disableWildcard : options.supportsWildcard;

  const withWildcard = value => {
    return useWildcard && value ? `*${value}*` : value;
  };

  // const pageNum = params.pageNumber;
  // const pageSize = params.resultsPerPage;
  // RIS page/pageSize pagination disabled: enriched study list returns fewer rows than
  // limit/offset. Re-enable when backend pagination matches full filtered totals.
  // const useRisPage =
  //   options.supportsRisPagination === true &&
  //   params.risStudyPagination === true &&
  //   typeof pageNum === 'number' &&
  //   pageNum >= 1 &&
  //   typeof pageSize === 'number' &&
  //   pageSize >= 1;

  const parameters = {
    // Named
    PatientName: withWildcard(params.patientName),
    //PatientID: withWildcard(params.patientId),
    '00100020': withWildcard(params.patientId), // Temporarily to make the tests pass with dicomweb-server.. Apparently it's broken?
    AccessionNumber: withWildcard(params.accessionNumber),
    StudyDescription: withWildcard(params.studyDescription),
    ModalitiesInStudy: params.modalitiesInStudy,
    // Other
    limit: params.limit || 101,
    offset: params.offset || 0,
    includefield: commaSeparatedFields, // serverSupportsQIDOIncludeField ? commaSeparatedFields : 'all',
  };

  if (options.supportsFuzzyMatching === true) {
    parameters.fuzzymatching = true;
  }

  // if (useRisPage) {
  //   parameters.page = pageNum;
  //   parameters.pageSize = pageSize;
  // }

  // build the StudyDate range parameter
  if (params.startDate && params.endDate) {
    parameters.StudyDate = `${params.startDate}-${params.endDate}`;
  } else if (params.startDate) {
    const today = new Date();
    const DD = String(today.getDate()).padStart(2, '0');
    const MM = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    const YYYY = today.getFullYear();
    const todayStr = `${YYYY}${MM}${DD}`;

    parameters.StudyDate = `${params.startDate}-${todayStr}`;
  } else if (params.endDate) {
    const oldDateStr = `19700102`;

    parameters.StudyDate = `${oldDateStr}-${params.endDate}`;
  }

  // Build the StudyInstanceUID parameter
  if (params.studyInstanceUid) {
    let studyUids = params.studyInstanceUid;
    studyUids = Array.isArray(studyUids) ? studyUids.join() : studyUids;
    studyUids = studyUids.replace(/[^0-9.]+/g, '\\');
    parameters.StudyInstanceUID = studyUids;
  }

  /*
   * RIS / studies proxy: extra query keys (not standard QIDO). Backend matches e.g.
   * report_status, modality_name, study_status, priority_status, assigned_id, assigned_name, ai_result
   */
  if (params.risStudyPagination === true) {
    if (params.reportStatus) {
      parameters.report_status = params.reportStatus;
    }
    const modalityNameForRis =
      params.modalityName ||
      (params.modalitiesInStudy?.length ? params.modalitiesInStudy.join(',') : undefined);
    if (modalityNameForRis) {
      parameters.modality_name = modalityNameForRis;
    }
    if (params.studyStatus) {
      parameters.study_status = params.studyStatus;
    }
    if (params.priorityStatus) {
      parameters.priority_status = params.priorityStatus;
    }
    if (params.assignedId) {
      parameters.assigned_id = params.assignedId;
    }
    if (params.assignedName) {
      parameters.assigned_name = params.assignedName;
    }
    if (params.aiResult) {
      parameters.ai_result = params.aiResult;
    }
  }

  // Clean query params of undefined values.
  const final = {};
  Object.keys(parameters).forEach(key => {
    if (parameters[key] !== undefined && parameters[key] !== '') {
      final[key] = parameters[key];
    }
  });

  return final;
}

export { mapParams, search, processResults };
